import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SmartLink from '../common/SmartLink'
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../lib/supabaseClient';
import { useRegisterEditor } from '../lib/EditorContext';
import { defaultContent } from '../lib/defaultContent';

const { FiTarget, FiActivity, FiSearch, FiPlayCircle } = FiIcons;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const defaultStrategies = [
  {
    id: 1,
    title: "Track Triggers",
    iconName: "FiTarget",
    desc: "Identify the patterns before the storm. Learn what sets off challenging behaviors so you can anticipate and proactively support your child.",
    iconBg: "bg-blue-100",
    iconBorder: "border-blue-200",
    iconColor: "text-blue-600"
  },
  {
    id: 2,
    title: "Regulate Emotions",
    iconName: "FiActivity",
    desc: "A dysregulated adult cannot regulate a dysregulated child. Discover physical and mental techniques to maintain your center in the heat of the moment.",
    iconBg: "bg-yellow-100",
    iconBorder: "border-yellow-200",
    iconColor: "text-yellow-600"
  },
  {
    id: 3,
    title: "Find Behavioral Patterns",
    iconName: "FiSearch",
    desc: "Look beneath the surface. Behavior is communication. We'll help you decode what your child is trying to say when words fail them.",
    iconBg: "bg-red-100",
    iconBorder: "border-red-200",
    iconColor: "text-brand-rose"
  }
];

const groundingExamples = [
  "Anchor breath: Inhale 4, exhale 6. Keep voice low.",
  "Feet-on-floor reset: Press feet down, bend knees.",
  "One-phrase grounding: 'Stay calm' / 'They’re having a hard time.'",
  "Micro body release: Unclench jaw, drop shoulders.",
  "Narrow focus: 'Just keep my voice steady for 2 minutes.'",
  "Sensory reset: Cold water on hands.",
  "After-moment discharge: Shake hands, step away 30 sec."
];

export default function ParentingStrategies() {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchContent() {
      try {
        const { data, error } = await supabase
          .from('page_content')
          .select('content')
          .eq('page_id', 'parenting-strategies')
          .single();

        if (error) {
          console.warn('Could not fetch strategies content, using defaults.', error.message);
        } else if (data && data.content) {
          setContent(data.content);
        }
      } catch (err) {
        console.error('Error fetching strategies content:', err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchContent();
  }, []);

  const title = content?.title || "Parenting Strategies";
  const subtitle = content?.subtitle || "Move away from reaction and step into intentional connection. Our strategies help you understand the 'why' behind the behavior.";
  const overline = content?.overline || "From Chaos to Connection";

  const currentStrategies = content?.strategies || defaultStrategies;
  const currentGrounding = content?.groundingExamples || groundingExamples;

  const voodooDollVenting = content?.voodooDollVenting || defaultContent["parenting-strategies"].voodooDollVenting;
  const bodyMappingPain = content?.bodyMappingPain || defaultContent["parenting-strategies"].bodyMappingPain;
  const wantToLearnMore = content?.wantToLearnMore || defaultContent["parenting-strategies"].wantToLearnMore;

  const handleSaveSuccess = (newContent) => {
    setContent(newContent);
  };

  useRegisterEditor(
    'parenting-strategies',
    { title, subtitle, overline, strategies: currentStrategies, groundingExamples: currentGrounding, voodooDollVenting, bodyMappingPain, wantToLearnMore },
    handleSaveSuccess,
    'Parenting Strategies'
  );

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <motion.div initial="hidden" animate="visible" exit="hidden" className="py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#333] dark:text-[#fff] font-bold tracking-widest uppercase text-xs mb-3 block">{overline}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-[#333] dark:text-[#fff] mb-6">{title}</h1>
          <p className="text-lg text-[#333] dark:text-[#fff]">
            {subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {currentStrategies.map((strategy) => (
            <motion.div key={strategy.id} variants={fadeUp} className="bg-white dark:bg-brand-slate rounded-3xl p-8 shadow-sm border border-sand-100 flex flex-col items-center text-center hover:shadow-lg transition-all duration-300">
              <div className={`w-16 h-16 ${strategy.iconBg} dark:bg-brand-slate rounded-full flex items-center justify-center mb-6 shadow-sm border ${strategy.iconBorder}`}>
                <SafeIcon icon={FiIcons[strategy.iconName] || FiIcons.FiTarget} className={`${strategy.iconColor} w-8 h-8`} />
              </div>
              <h3 className="text-2xl font-bold text-[#333] dark:text-[#fff] mb-4">{strategy.title}</h3>
              <p className="text-[#333] dark:text-[#fff] leading-relaxed">{strategy.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div variants={fadeUp} className="mb-20 max-w-5xl mx-auto grid md:grid-cols-2 gap-8 items-start">
          <div className="bg-white dark:bg-brand-slate rounded-3xl p-8 shadow-sm border border-sand-100">
            <h3 className="text-2xl font-bold text-[#333] dark:text-[#fff] mb-6 flex items-center gap-3">
              <span className="text-3xl">🧘</span> Grounding Techniques
            </h3>
            <div className="space-y-4">
              {currentGrounding.map((technique, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="mt-1 text-brand-rose">
                    <span className="text-lg">🌿</span>
                  </div>
                  <p className="text-[#333] dark:text-[#fff] font-medium">{technique}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-white dark:bg-brand-slate rounded-3xl p-8 shadow-sm border border-sand-100">
              <h3 className="text-2xl font-bold text-[#333] dark:text-[#fff] mb-4 flex items-center gap-3">
                <span className="text-3xl">🗣️</span> {voodooDollVenting.title}
              </h3>
              <p className="text-[#333] dark:text-[#fff] mb-4">{voodooDollVenting.description}</p>
              <div className="bg-[#fef7e8] dark:bg-brand-slate/50 p-4 rounded-xl border border-sand-100 italic text-[#333] dark:text-[#fff] opacity-80">
                {voodooDollVenting.quote}
              </div>
            </div>

            <div className="bg-white dark:bg-brand-slate rounded-3xl p-8 shadow-sm border border-sand-100">
              <h3 className="text-2xl font-bold text-[#333] dark:text-[#fff] mb-4 flex items-center gap-3">
                <span className="text-3xl">📍</span> {bodyMappingPain.title}
              </h3>
              <p className="text-[#333] dark:text-[#fff] mb-4">{bodyMappingPain.description}</p>
              <ul className="space-y-2 text-[#333] dark:text-[#fff] font-medium list-disc list-inside marker:text-brand-rose">
                {bodyMappingPain.questions.map((q, i) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="bg-brand-rose text-white rounded-[3rem] p-10 md:p-16 text-center max-w-4xl mx-auto shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">{wantToLearnMore.title}</h2>
            <p className="text-lg mb-10 max-w-2xl mx-auto opacity-90">
              {wantToLearnMore.description}
            </p>
            <SmartLink to={wantToLearnMore.buttonLink} className="inline-flex items-center gap-2 px-10 py-4 bg-white text-brand-rose rounded-full font-bold text-lg hover:scale-105 transition-transform shadow-lg">
              <SafeIcon icon={FiPlayCircle} /> {wantToLearnMore.buttonText}
            </SmartLink>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}